import { useQuery } from "@apollo/client";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { ProcessedManifestCorrectionStatsByVendorQuery } from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";

import { PROCESSED_MANIFEST_CORRECTION_STATS_BY_VENDOR_QUERY } from "./api";
import { upsertOmnisearchTerm } from "./omnisearch";

interface Props {
  shippedYear: number;
  skip?: boolean;
}

type VendorRow =
  ProcessedManifestCorrectionStatsByVendorQuery["processedManifestCorrectionStatsByVendor"][number];

export function VendorBreakdownTile({ shippedYear, skip = false }: Props) {
  const { tenantId } = useTenant();
  const { filters, setFilters } = useQueryParams<{ omnisearch?: string }>();

  const { data, loading, error } = useQuery(PROCESSED_MANIFEST_CORRECTION_STATS_BY_VENDOR_QUERY, {
    variables: { tenantId: tenantId ?? "", shippedYear },
    skip: skip || !tenantId,
  });

  const filterByVendor = (vendor: string) => {
    // Only add the vendor term. tenantId + shippedDate come from the grid's
    // defaultSearch (smart-merged into the query), so injecting them here would
    // just surface a redundant tenantId chip in the search input.
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(prev.omnisearch ?? "", "vendor", vendor),
    }));
  };

  return (
    <DashboardTile size={{ xs: 12, md: 12, lg: 8 }}>
      <DashboardTile.Header title="By Vendor" />
      <DashboardTile.ContentArea>
        {renderBody({ loading, error, data, shippedYear, onVendorClick: filterByVendor })}
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
}

function renderBody({
  loading,
  error,
  data,
  shippedYear,
  onVendorClick,
}: {
  loading: boolean;
  error: unknown;
  data: ProcessedManifestCorrectionStatsByVendorQuery | undefined;
  shippedYear: number;
  onVendorClick: (vendor: string) => void;
}) {
  if (loading) {
    return (
      <Stack spacing={1} data-testid="vendor-breakdown-skeleton">
        <Skeleton variant="rectangular" height={32} />
        <Skeleton variant="rectangular" height={32} />
        <Skeleton variant="rectangular" height={32} />
      </Stack>
    );
  }

  if (error) {
    return <Typography color="error">Couldn't load vendor breakdown</Typography>;
  }

  const rows = data?.processedManifestCorrectionStatsByVendor ?? [];
  const activeRows = rows.filter((r) => r.onTime + r.late + r.overdue + r.inWindow > 0);

  if (activeRows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No vendor activity in {shippedYear}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Vendor</TableCell>
          <TableCell align="right">Compliance</TableCell>
          <TableCell align="right">Resolved on time / concluded</TableCell>
          <TableCell align="right">Pending</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {activeRows.map((row, index) => (
          <VendorTableRow
            key={row.vendor ?? `__unmatched-${index}`}
            row={row}
            onVendorClick={onVendorClick}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function VendorTableRow({
  row,
  onVendorClick,
}: {
  row: VendorRow;
  onVendorClick: (vendor: string) => void;
}) {
  const concluded = row.onTime + row.late + row.overdue;
  const percent = concluded > 0 ? Math.round((row.onTime / concluded) * 100) : null;
  const vendor = row.vendor;
  const clickable = vendor !== null && vendor !== undefined;

  return (
    <TableRow
      hover={clickable}
      onClick={clickable ? () => onVendorClick(vendor) : undefined}
      sx={clickable ? { cursor: "pointer" } : undefined}
    >
      <TableCell>
        {clickable ? (
          vendor
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            No matched vendor
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{percent !== null ? `${percent}%` : "—"}</TableCell>
      <TableCell align="right">
        {row.onTime} / {concluded}
      </TableCell>
      <TableCell align="right">{row.inWindow}</TableCell>
    </TableRow>
  );
}
